/**
 * Hook for fetching project module tab counts (All Projects).
 * Provides counts for the projects module landing page tabs.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/api-client';
import { useAuth } from '@/store/auth.store';
import { useOrganizationStore } from '@/store/organization.store';

export interface ProjectModuleCounts {
  allProjects: number;
  templates: number;
}

interface UseProjectModuleCountsOptions {
  enabled?: boolean;
}

// Query key for project module counts
const projectModuleCountsKey = ['project-module-counts'] as const;

/**
 * Fetches project module counts
 */
async function fetchProjectModuleCounts(): Promise<ProjectModuleCounts> {
  return apiClient<ProjectModuleCounts>('/projects/module-counts');
}

/**
 * Hook for fetching project module counts.
 * Returns count for "All Projects".
 */
export function useProjectModuleCounts({ enabled = true }: UseProjectModuleCountsOptions = {}) {
  const queryClient = useQueryClient();
  const user = useAuth((state) => state.user);
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization);

  const { data, isLoading, error } = useQuery({
    queryKey: [...projectModuleCountsKey, currentOrganization?.id, user?.id],
    queryFn: fetchProjectModuleCounts,
    enabled: enabled && !!currentOrganization?.id && !!user?.id,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  const counts = {
    allProjects: data?.allProjects,
    templates: data?.templates,
  };

  /**
   * Invalidate and refetch counts
   */
  const refetchCounts = () => {
    queryClient.invalidateQueries({ queryKey: projectModuleCountsKey });
  };

  return {
    counts,
    isLoading,
    error,
    refetchCounts,
  };
}

// Export query key for external use
export { projectModuleCountsKey };
